export const AUTH_USER_SNAPSHOT_KEY = "manus-runtime-user-info";
export const IMAGE_CANDIDATE_HANG_MS = 1500;

export function parsePersistedSupabaseAccessToken(raw: string | null | undefined): string | null {
  if (!raw?.trim()) return null;
  try {
    const parsed = JSON.parse(raw) as {
      access_token?: unknown;
      currentSession?: { access_token?: unknown };
    };
    const token = parsed.access_token ?? parsed.currentSession?.access_token;
    return typeof token === "string" && token.length > 0 ? token : null;
  } catch {
    return null;
  }
}

export function readPersistedSupabaseAccessToken(
  storage: Pick<Storage, "length" | "key" | "getItem"> | null | undefined,
): string | null {
  if (!storage) return null;
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (!key?.startsWith("sb-") || !key.includes("auth-token")) continue;
    const token = parsePersistedSupabaseAccessToken(storage.getItem(key));
    if (token) return token;
  }
  return null;
}

export type SessionLike = { access_token: string } | null;

export type SessionTokenSource = {
  seedToken?: string | null;
  getSession: () => Promise<SessionLike>;
  onAuthStateChange: (cb: (session: SessionLike) => void) => { unsubscribe: () => void };
};

export type SessionTokenCache = {
  getAccessToken: () => string | null;
  ensureAccessToken: () => Promise<string | null>;
  subscribe: (cb: (token: string | null) => void) => () => void;
};

export function createSessionTokenCache(source: SessionTokenSource): SessionTokenCache {
  let token = source.seedToken ?? null;
  let confirmEpoch = 0;
  const listeners = new Set<(next: string | null) => void>();

  const setToken = (next: string | null) => {
    if (token === next) return;
    token = next;
    for (const listener of listeners) listener(token);
  };

  source.onAuthStateChange((session) => {
    if (session?.access_token) {
      confirmEpoch += 1;
      setToken(session.access_token);
      return;
    }
    const epoch = ++confirmEpoch;
    void source.getSession().then((confirmed) => {
      if (epoch !== confirmEpoch) return;
      setToken(confirmed?.access_token ?? null);
    });
  });

  return {
    getAccessToken: () => token,
    async ensureAccessToken() {
      if (token) return token;
      const session = await source.getSession();
      setToken(session?.access_token ?? null);
      return token;
    },
    subscribe(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
  };
}

/** True only after auth.me has settled with a user. A stored snapshot does not count. */
export function isServerSessionConfirmed(query: {
  isFetched: boolean;
  isLoading: boolean;
  data: unknown;
}): boolean {
  return query.isFetched && !query.isLoading && query.data != null;
}

/** Keep last known user while auth.me is in flight; clear when settled empty. */
export function nextAuthUserSnapshot<T>(
  current: T | null | undefined,
  stored: T | null | undefined,
  loading: boolean,
): T | null {
  if (current) return current;
  if (loading && stored) return stored;
  return null;
}

export function shouldAdvanceHungImageCandidate(elapsedMs: number, timeoutMs: number): boolean {
  return elapsedMs >= timeoutMs;
}

export function readStoredAuthUserSnapshot<T>(
  storage: Pick<Storage, "getItem"> | null | undefined,
): T | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(AUTH_USER_SNAPSHOT_KEY);
    if (!raw || raw === "null") return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function writeStoredAuthUserSnapshot(storage: Pick<Storage, "setItem">, value: unknown): void {
  storage.setItem(AUTH_USER_SNAPSHOT_KEY, JSON.stringify(value ?? null));
}
